import React, { useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';

interface NewsItem {
  id: number;
  title: string;
  body: string;
  emoji: string;
  emojiColor: string;
  isPinned: boolean;
  authorId: number;
  createdAt: string;
}

interface Props {
  news: NewsItem;
  access: any;
  onClose: () => void;
  onSuccess: (news: NewsItem) => void;
}

const EMOJI_CATEGORIES: Record<string, string[]> = {
  General: ['📢','📣','📌','🔔','💬','📝','📋','✅','⭐','🎯','💡','🗣️','🔖','💎','📍'],
  Work:    ['💼','📊','📈','📉','🖥️','🤝','👥','🏆','🥇','💰','🔧','📦','🚀','⚡','🎪'],
  Learn:   ['🎓','📚','🧠','✏️','🔬','📖','🎒','🏫','💻','🧩','🔭','📐','✍️','🎨','🔍'],
  Events:  ['🎉','🎊','🎈','🥳','🎂','🎁','🎤','🎭','🏖️','🥂','🍾','🎶','🌟','🏅','🎠'],
  Other:   ['❗','⚠️','🔴','🟢','🔵','🟡','🛡️','🔑','🔒','⏰','🗺️','💫','🌈','🌐','📡'],
};

const COLOR_OPTIONS = [
  { bg: 'rgba(239,68,68,.12)' },
  { bg: 'rgba(245,158,11,.12)' },
  { bg: 'rgba(16,185,129,.12)' },
  { bg: 'rgba(37,99,235,.12)' },
  { bg: 'rgba(124,58,237,.12)' },
  { bg: 'rgba(219,39,119,.12)' },
  { bg: 'rgba(14,165,233,.12)' },
];

export default function EditNews({ news, access, onClose, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [title, setTitle] = useState(news.title);
  const [body, setBody] = useState(news.body);
  const [emoji, setEmoji] = useState(news.emoji);
  const [emojiColor, setEmojiColor] = useState(news.emojiColor);
  const [isPinned, setIsPinned] = useState(news.isPinned);
  const [emojiTab, setEmojiTab] = useState('General');
  const [loading, setLoading] = useState(false);

  const isAdmin = access?.dashboard_settings === true || access?.dashboard_settings === 1;

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      enqueueSnackbar(t('FillRequired') || 'Please fill in all required fields', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.put(`news/${news.id}`, { title: title.trim(), body: body.trim(), emoji, emojiColor, isPinned });
      enqueueSnackbar(t('news_save') || 'Saved', { variant: 'success' });
      onSuccess(data);
      onClose();
    } catch {
      enqueueSnackbar(t('err_save_generic') || 'Failed to save', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(4,9,26,.45)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-form)', border: '1px solid var(--border-input)', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,.18)' }}>

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: emojiColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            {emoji}
          </div>
          <div style={{ flex: 1, fontSize: 16, fontWeight: 800, color: 'var(--text-color)', letterSpacing: '-.01em' }}>
            {t('news_edit') || 'Edit announcement'}
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid var(--border-input)', background: 'var(--btn-user-form)', color: 'var(--text-color)', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Preview */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>
              {t('news_preview_label') || 'Preview'}
            </div>
            <div style={{ background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', borderRadius: 10, padding: 14, display: 'flex', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: emojiColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color)', lineHeight: 1.3 }}>{title || '…'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar)', marginTop: 5, lineHeight: 1.6 }}>{body || '…'}</div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              {t('news_title_label') || 'Title'} *
            </label>
            <input
              className="input-timesheet"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontSize: 14 }}
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={255}
            />
          </div>

          {/* Body */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              {t('news_body_label') || 'Message'} *
            </label>
            <textarea
              className="input-timesheet"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-color)', fontSize: 14, minHeight: 90, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
              value={body}
              onChange={e => setBody(e.target.value)}
            />
          </div>

          {/* Emoji + Color row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Emoji picker */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                {t('news_emoji_label') || 'Icon (emoji)'}
              </div>
              <div style={{ border: '1px solid var(--border-input)', borderRadius: 10, overflow: 'hidden', background: 'var(--btn-user-form)' }}>
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-input)', padding: '6px 8px 0', gap: 2, background: 'var(--bg-form)', overflowX: 'auto' }}>
                  {Object.keys(EMOJI_CATEGORIES).map(cat => (
                    <button
                      key={cat}
                      onClick={() => setEmojiTab(cat)}
                      style={{ padding: '5px 10px', borderRadius: '7px 7px 0 0', fontSize: 10, fontWeight: 700, cursor: 'pointer', border: 'none', background: emojiTab === cat ? 'rgba(37,99,235,.1)' : 'transparent', color: emojiTab === cat ? '#2563eb' : 'var(--text-color-calendar-week)', borderBottom: emojiTab === cat ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2, padding: 8, maxHeight: 140, overflowY: 'auto' }}>
                  {EMOJI_CATEGORIES[emojiTab].map(em => (
                    <button
                      key={em}
                      onClick={() => setEmoji(em)}
                      title={em}
                      style={{ aspectRatio: '1', borderRadius: 7, border: 'none', background: em === emoji ? 'rgba(37,99,235,.12)' : 'transparent', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .1s', boxShadow: em === emoji ? '0 0 0 2px rgba(37,99,235,.35)' : 'none' }}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Color picker */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
                {t('news_color_label') || 'Icon background'}
              </div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
                {COLOR_OPTIONS.map(({ bg }) => (
                  <button
                    key={bg}
                    onClick={() => setEmojiColor(bg)}
                    style={{ width: 32, height: 32, borderRadius: 8, background: bg, cursor: 'pointer', border: emojiColor === bg ? '2px solid var(--text-color)' : '2px solid transparent', transform: emojiColor === bg ? 'scale(1.15)' : 'scale(1)', transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}
                  >
                    {emojiColor === bg ? '✓' : ''}
                  </button>
                ))}
              </div>
              <div style={{ padding: 10, borderRadius: 10, background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginBottom: 8 }}>Selected</div>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: emojiColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, margin: '0 auto' }}>
                  {emoji}
                </div>
              </div>
            </div>

          </div>

          {/* Pin toggle — only for news_admin */}
          {isAdmin && (
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-color)' }}>📌 {t('news_pin_label') || 'Pin this announcement'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 2 }}>{t('news_pin_hint') || 'Pinned posts always appear first'}</div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: 40, height: 22, cursor: 'pointer', flexShrink: 0 }}>
                <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                <span style={{ position: 'absolute', inset: 0, borderRadius: 11, background: isPinned ? '#2563eb' : 'var(--border-input)', transition: 'background .2s' }} />
                <span style={{ position: 'absolute', top: 3, left: isPinned ? 21 : 3, width: 16, height: 16, borderRadius: 8, background: 'white', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.2)' }} />
              </label>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-input)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            className="bg-[var(--btn-user-btn)] text-[var(--text-color)] py-[5px] px-[15px] rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)] border-none text-[13px] font-semibold"
          >
            {t('Cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ padding: '7px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: 'white', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? '…' : (t('news_save') || 'Save changes')}
          </button>
        </div>

      </div>
    </div>
  );
}
